import {Component, computed, inject, Injector, linkedSignal, OnInit, signal} from "@angular/core";
import {BasicMaterialDialog} from "../../basic-material-dialog/basic-material-dialog.directive";
import {SessionDialogInput, SessionDialogOutput} from "@common/models/session-dialog";
import {MultiRoomRecordingSessionDialogStore} from "./store/mrr-session-dialog.store";
import {Translations} from "@translations";
import {FetchDataType, SimUser} from "@models";
import {SimMaterialDialogService, SimSessionService} from "@services";
import {DialogData_DetailedRoomSelectionOutput} from "../../general-dialogs/detailed-room-selection-dialog/detailed-room-selection-dialog.component";
import {SimChipAutocompleteComponent} from "../../../../../shared/shared-components/sim-advanced-inputs/autocomplete/sim-chip-autocomplete/sim-chip-autocomplete.component";
import {FetchData_TypeMapping} from "../../../../../shared/shared-components/sim-advanced-inputs/autocomplete/autocomplete.service";

@Component({
	selector: "sim-multi-room-recording-session-dialog",
	standalone: false,
	providers: [MultiRoomRecordingSessionDialogStore],
	templateUrl: "./multi-room-recording-session-dialog.component.html",
	styleUrl: "./multi-room-recording-session-dialog.component.scss",
})
export class MultiRoomRecordingSessionDialogComponent
	extends BasicMaterialDialog<MultiRoomRecordingSessionDialogComponent, SessionDialogInput, SessionDialogOutput>
	implements OnInit
{
	// Constants
	public readonly T = Translations;
	protected readonly FetchDataType = FetchDataType;

	// Properties
	protected readonly eventEndDate = signal(this.data.eventEndDate).asReadonly();
	protected readonly eventStartDate = signal(this.data.eventStartDate).asReadonly();
	protected readonly viewParticipants = linkedSignal<SimUser[]>(
		() =>
			this.store
				.session()
				?.participants.filter((p) => p.user)
				.map((p) => p.user) ?? [],
	);
	protected readonly loadedForms = signal<SimChipAutocompleteComponent<keyof FetchData_TypeMapping>[]>([]);
	protected readonly areFormsLoaded = computed<boolean>(() => this.computeAreFormsLoaded());
	protected readonly closePromise = signal<Promise<unknown> | null>(null);

	// Injections
	protected readonly store = inject(MultiRoomRecordingSessionDialogStore);
	readonly #dialogService = inject(SimMaterialDialogService);
	readonly #sessionService = inject(SimSessionService);

	constructor(injector: Injector) {
		super(injector);
	}

	ngOnInit() {
		super.ngOnInit();
		this.store.storeInitialized(this.data.eventDay, this.data.eventId, this.data.sessionId);
	}

	public async closeDialog(): Promise<void> {
		const sessionId = this.store.session()?.id;
		if (sessionId) {
			const promise = this.#sessionService.getSessionById(sessionId);
			this.closePromise.set(promise);
			const outputSession = await promise;
			this.dialogRef.close({action: "ok", outputSession});
		} else {
			this.dialogRef.close({action: "cancel"});
		}
	}

	protected openRoomDetailDialog(): void {
		const tempRoom = this.store.currentStationForm()?.controls.room.getRawValue();
		this.#dialogService
			.openDetailedTrainingRoomSelection(
				{
					preselectedTrainingRooms: tempRoom ? [tempRoom] : [],
					excludeRoomIds: [],
					singleRoom: true,
				},
				{width: "1100px", height: "825px"},
			)
			.then((data: DialogData_DetailedRoomSelectionOutput) => {
				const selection = data.trainingRoomSelection?.at(0);
				if (selection) {
					this.store.currentStationForm()?.controls.room.setValue(selection);
				}
			});
	}

	protected addParticipant(participant: SimUser): void {
		this.viewParticipants.update((value) => [...new Set([...value, participant])]);
		this.store.participantAdded(participant);
	}

	protected removeParticipant(userId: string): void {
		this.viewParticipants.update((value) => value.filter((p) => p.id !== userId));
		this.store.participantRemoved(userId);
	}

	protected onFormLoad(formField: SimChipAutocompleteComponent<keyof FetchData_TypeMapping>) {
		if (formField.formCtrl()?.value) {
			this.loadedForms.update((previousForms) => [...previousForms, formField]);
		}
	}

	private computeAreFormsLoaded(): boolean {
		const currentForm = this.store.currentStationForm();
		if (!currentForm) {
			return true;
		}
		let result = true;
		if (currentForm.value.room?.id) {
			result = result && this.loadedForms().some((form) => form.selectedOption?.id === currentForm.value.room?.id);
		}
		if (currentForm.value.scenario?.id) {
			result = result && this.loadedForms().some((form) => form.selectedOption?.id === currentForm.value.scenario?.id);
		}
		return result;
	}
}
